import React, { useRef, useState, useEffect } from 'react';
import { SxProps } from '@mui/system';
import { styled, Theme } from '@mui/material/styles';
import MenuList from '@mui/material/MenuList';
import { NavItem } from '../nav';
import { SideNavButton } from './internal/SideNavButton';
import { SideNavPopover } from './internal/SideNavPopover';
import { useNavItems } from 'src/views/home/HomeWrapper/navItems';

type Props = {
  activeSideNavItem: NavItem | undefined;
  headerHeight?: number;
  hoverRequired?: boolean;
  isAuthenticated?: boolean;
  isHelpNavItemVisible?: boolean;
  onHelpClick?: () => void;
  onNavItemClick: (navItem: NavItem) => void;
  setActiveSideNavItem: (activeSideNavItem: NavItem | undefined) => void;
  sx?: SxProps<Theme>;
  variant?: 'primary' | 'secondary';
};

const HELP = 'help';
const LEFT_SIDE_NAV = 'left-side-nav';

export function LeftSideNav(props: Readonly<Props>) {
  const {
    activeSideNavItem,
    headerHeight = 100,
    hoverRequired,
    isAuthenticated = false,
    onHelpClick: onHelpNavItemClick,
    onNavItemClick: onNavItemClickProp,
    setActiveSideNavItem,
    sx,
    variant = 'primary',
  } = props;
  const menuItem: NavItem = { key: LEFT_SIDE_NAV };
  const navItems = useNavItems();
  const [openItems, setOpenItems] = useState<NavItem[]>([]);
  const [activeTopItem, setActiveTopItem] = useState<NavItem | undefined>(
    activeSideNavItem
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredItem, setHoveredItem] = useState<NavItem | null>(null);
  const stackRef = useRef(null);
  const isPrimary = variant === 'primary';

  useEffect(() => {
    setActiveTopItem(activeSideNavItem);
  }, [activeSideNavItem]);

  const onNavItemClick = (
    navItem: NavItem,
    level: number,
    event?: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl(event?.currentTarget ?? null);

    if (navItem.key === HELP) {
      setActiveTopItem(navItem);
      onHelpNavItemClick?.();
    } else {
      if (level === 0) {
        setActiveTopItem(navItem);
      }
      if (openItems?.[0]?.key === navItem?.key) {
        setActiveTopItem(undefined);
        setOpenItems([]);
      } else if (!navItem.navItems || navItem.route || navItem.url) {
        setActiveSideNavItem(level === 0 ? navItem : activeTopItem);
        onNavItemClickProp(navItem);
        setOpenItems([]);
        setHoveredItem(null);
      } else {
        setOpenItems([navItem]);
      }
    }
  };

  const onNavItemHover = (navItem: NavItem, level: number) => {
    const newOpenItems = [...openItems];
    newOpenItems.length = level;
    if (navItem.navItems) {
      newOpenItems.push(navItem);
    }
    setOpenItems(newOpenItems);
    setTimeout(() => {
      setOpenItems([...newOpenItems]);
    }, 1);
  };

  return (
    <>
      <SytledMenuList
        aria-busy="true"
        className="sidenav-base-menulist"
        disablePadding
        isHover={!!hoveredItem}
        isPrimary={isPrimary}
        onMouseEnter={() => {
          if (!hoveredItem && hoverRequired) {
            setHoveredItem(menuItem);
          }
        }}
        onMouseLeave={() => {
          if ((!openItems || openItems?.length === 0) && hoverRequired) {
            setHoveredItem(null);
          }
        }}
        onMouseOver={() => {
          if (!hoveredItem && hoverRequired) {
            setHoveredItem(menuItem);
          }
        }}
        ref={stackRef}
        sx={sx}
      >
        {navItems.map((navItem) => {
          const isOpen = navItem === openItems[0];
          const isActive = navItem.key === activeTopItem?.key;
          return (
            <SideNavButton
              active={isActive}
              data-testid={`${navItem.key}-MenuItem`}
              hasItems={!!navItem.navItems && navItem.navItems?.length > 0}
              hidden={navItem.hidden}
              hoveredItem={hoveredItem}
              isOpen={isOpen || isActive}
              isPrimary={isPrimary}
              key={navItem.key}
              navItem={navItem}
              onClick={(e) => onNavItemClick(navItem, 0, e)}
              setHoveredItem={setHoveredItem}
            >
              {navItem.label ?? navItem.key}
            </SideNavButton>
          );
        })}
      </SytledMenuList>

      <SideNavPopover
        anchorEl={anchorEl ?? stackRef.current}
        anchorTopPosition={anchorEl?.getBoundingClientRect().top ?? 0}
        headerHeight={headerHeight}
        isAuthenticated={isAuthenticated}
        isPrimary={isPrimary}
        onClose={() => {
          setActiveTopItem(activeSideNavItem);
          setOpenItems([]);
          setHoveredItem(null);
        }}
        onNavItemClick={onNavItemClick}
        onNavItemHover={onNavItemHover}
        open={Boolean(openItems.length)}
        openItems={openItems}
      />
    </>
  );
}

interface VariantsProps {
  isPrimary?: boolean;
}

interface AvailabilityProps {
  isHover?: boolean;
}

interface StyledProps extends VariantsProps, AvailabilityProps {
  isActive?: boolean;
}

const getWidth = (
  isHover: boolean | undefined,
  isPrimary: boolean | undefined
) => {
  if (isPrimary && isHover) return 240;
  else if (!isPrimary && isHover) return 260;
  else return 64;
};
const SytledMenuList = styled(MenuList, {
  shouldForwardProp: (props) => props !== 'isPrimary' && props !== 'isHover',
})<StyledProps>(({ isHover, isPrimary, theme }) => ({
  '& div.MuiListItemButton-root': {
    '&.active': {
      '&:hover': {
        '& > *': {
          backgroundColor: isPrimary
            ? `${theme.palette.primary[50]} !important`
            : theme.palette.common.white,
        },
      },
      '&:not(:first-of-type)': {
        '& svg > path': {
          '&:not(:last-child)': {
            fill: isPrimary
              ? theme.palette.primary[900]
              : theme.palette.primary.main,
          },
        },
      },
      backgroundColor: isPrimary
        ? `${theme.palette.primary[50]} !important`
        : theme.palette.common.white,
    },
    '&:first-of-type': {
      '&:hover': {
        '& > *': {
          backgroundColor: isPrimary
            ? theme.palette.grey[50]
            : theme.palette.common.white,
        },
        backgroundColor: isPrimary
          ? theme.palette.grey[50]
          : theme.palette.common.white,
      },
    },
    '&:not(:first-of-type)': {
      '&:hover': {
        '& > *': {
          backgroundColor: isPrimary
            ? theme.palette.grey[50]
            : theme.palette.common.white,
        },
        '& svg > path': {
          '&:first-of-type': {
            fill: isPrimary
              ? theme.palette.primary[900]
              : theme.palette.primary.main,
          },
          '&:not(:last-child)': {
            fill: isPrimary
              ? theme.palette.primary[900]
              : theme.palette.primary.main,
          },
        },
        backgroundColor: isPrimary
          ? theme.palette.grey[50]
          : theme.palette.common.white,
      },
    },
  },
  alignItems: 'center',
  backgroundColor: isPrimary
    ? theme.palette.common.white
    : theme.palette.neutral[10],
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: isPrimary ? '0 8px' : 0,
  width: getWidth(isHover, isPrimary),
}));
